import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { exportAsImage } from "../../utils/exportAsImage";

const AGE_GROUPS = [
  "BU9","GU9","BU11","GU11","BU13","GU13",
  "BU15","GU15","BU17","GU17",
];

export default function AgeGroupPerformance({ results, ajssRankings }) {
  const [visibleCategories, setVisibleCategories] = useState({
    AJSS: true,
    National: true,
    Overall: true,
  });

  const toggleCategory = (cat) => {
    setVisibleCategories((prev) => ({
      ...prev,
      [cat]: !prev[cat],
    }));
  };

  // Compute AJSS summary
  const ajssSummary = useMemo(() => {
    const groups = {};
    ajssRankings.forEach((r) => {
      if (!r.ageGroup) return;
      if (!groups[r.ageGroup]) groups[r.ageGroup] = [];
      if (typeof r.rank === "number" && !isNaN(r.rank)) {
        groups[r.ageGroup].push(r.rank);
      }
    });

    return AGE_GROUPS.filter((g) => groups[g]).map((ageGroup) => {
      const ranks = groups[ageGroup];
      const noPlayers = ranks.length;
      const avgRank = noPlayers
        ? (ranks.reduce((a, b) => a + b, 0) / noPlayers).toFixed(1)
        : null;
      const bestRank = noPlayers ? Math.min(...ranks) : null;

      // count medals for this age group
      const medalsWon = results.filter(
        (r) => r.ageGroup === ageGroup && r.medal
      ).length;

      return {
        ageGroup,
        noPlayers,
        avgRank,
        bestRank,
        medalsWon,
      };
    });
  }, [ajssRankings, results]);

  // Chart data for line chart (avg AJSS rank)
  const lineChartData = ajssSummary.map((row) => ({
    ageGroup: row.ageGroup,
    avgRank: row.avgRank ? parseFloat(row.avgRank) : null,
  }));

  // Chart data for medals grouped bar chart
  const medalSummary = useMemo(() => {
    const groups = {};
    results.forEach((r) => {
      if (!r.ageGroup || !r.medal) return;
      if (!groups[r.ageGroup]) {
        groups[r.ageGroup] = {
          ageGroup: r.ageGroup,
          AJSS: 0,
          National: 0,
          Overall: 0,
        };
      }
      if (r.rankingType === "AJSS") groups[r.ageGroup].AJSS++;
      else if (r.rankingType === "National") groups[r.ageGroup].National++;
      groups[r.ageGroup].Overall++;
    });

    return AGE_GROUPS.filter((g) => groups[g]).map((g) => groups[g]);
  }, [results]);

  return (
    <div className="space-y-6">
      {/* Save Button */}
      <div className="text-right">
        <button
          onClick={() => exportAsImage("age-group-performance-section", "age-group-performance")}
          className="px-3 py-1 bg-blue-500 text-white rounded"
        >
          Save as Image
        </button>
      </div>

      <div className="space-y-6" id="age-group-performance-section">
        {/* Chart 1: Average AJSS Rank per Age Group */}
        <div className="p-4 bg-white shadow rounded-xl">
          <h1 className="mb-4">Average Rank per Age Group (AJSS)</h1>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={lineChartData}>
              <XAxis dataKey="ageGroup" />
              <YAxis reversed allowDecimals={false} /> {/* lower rank = better */}
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="avgRank"
                stroke="#6366f1"
                name="Avg AJSS Rank"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2: Medals per Age Group */}
        <div className="p-4 bg-white shadow rounded-xl">
          <h1 className="mb-2">Total Medals Won per Age Group</h1>

          {/* Checkboxes for toggle */}
          <div className="flex flex-wrap gap-4 mb-4 no-export">
            {["AJSS", "National", "Overall"].map((cat) => (
              <label key={cat} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={visibleCategories[cat]}
                  onChange={() => toggleCategory(cat)}
                />
                {cat}
              </label>
            ))}
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={medalSummary}>
              <XAxis dataKey="ageGroup" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />

              <Bar
                dataKey="AJSS"
                fill="#6366f1"
                hide={!visibleCategories.AJSS}
              />
              <Bar
                dataKey="National"
                fill="#22c55e"
                hide={!visibleCategories.National}
              />
              <Bar
                dataKey="Overall"
                fill="#f59e0b"
                hide={!visibleCategories.Overall}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
